import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { LoginForm } from './components/auth/LoginForm';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Sidebar } from './components/layout/Sidebar';
import { Header } from './components/layout/Header';
import { Dashboard } from './components/admin/Dashboard';
import { InvoicesList } from './components/admin/InvoicesList';
import { QuotesList } from './components/admin/QuotesList';
import { ClientsList } from './components/admin/ClientsList';
import { PaymentsList } from './components/admin/PaymentsList';
import { ExpensesList } from './components/admin/ExpensesList';
import { Settings } from './components/admin/Settings';
import { ClientPortal } from './components/client/ClientPortal';
import { ClientInvoices } from './components/client/ClientInvoices';
import { ClientQuotes } from './components/client/ClientQuotes';
import { ClientPayments } from './components/client/ClientPayments';
import { ClientSupport } from './components/client/ClientSupport';
import { ClientAIAssistant } from './components/client/ClientAIAssistant';
import { AIAssistantButton } from './components/ai/AIAssistantButton';

function App() {
  const { user, loading } = useAuth();

  console.log('App render - User:', user?.id, 'Loading:', loading, 'Role:', user?.role, 'Email:', user?.email);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading application...</p>
          <p className="text-sm text-gray-500 mt-2">Checking authentication status</p>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log('No user, showing login form');
    return (
      <Router>
        <Routes>
          <Route path="/login" element={<LoginForm />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    );
  }

  // Special case for admin@aicrm.com
  if (user.email === 'admin@aicrm.com') {
    console.log('Admin user detected by email, showing admin app');
    return (
      <Router>
        <div className="flex min-h-screen bg-gray-50">
          <Sidebar />
          <div className="flex-1 flex flex-col">
            <Header />
            <main className="flex-1 p-6 overflow-auto">
              <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/invoices" element={<InvoicesList />} />
                <Route path="/quotes" element={<QuotesList />} />
                <Route path="/clients" element={<ClientsList />} />
                <Route path="/expenses" element={<ExpensesList />} />
                <Route path="/payments" element={<PaymentsList />} />
                <Route path="/ai-assistant" element={
                  <div className="text-center py-12">
                    <h2 className="text-2xl font-bold text-gray-900">AI Assistant</h2>
                    <p className="text-gray-600 mt-2">Use the floating AI assistant button in the bottom right corner</p>
                  </div>
                } />
                <Route path="/settings" element={<Settings />} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </main>
          </div>
          <AIAssistantButton />
        </div>
      </Router>
    );
  }

  console.log('User authenticated, showing main app for role:', user.role);

  return (
    <Router>
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col">
          <Header />
          <main className="flex-1 p-6 overflow-auto">
            <Routes>
              {/* Admin Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoices"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <InvoicesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/quotes"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <QuotesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clients"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <ClientsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/expenses"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <ExpensesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <PaymentsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-assistant"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <div className="text-center py-12">
                      <h2 className="text-2xl font-bold text-gray-900">AI Assistant</h2>
                      <p className="text-gray-600 mt-2">Use the floating AI assistant button in the bottom right corner</p>
                    </div>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Settings />
                  </ProtectedRoute>
                }
              />

              {/* Client Routes */}
              <Route
                path="/client-portal"
                element={
                  <ProtectedRoute requiredRole="client">
                    <ClientPortal />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/client-portal/invoices"
                element={
                  <ProtectedRoute requiredRole="client">
                    <ClientInvoices />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/client-portal/quotes"
                element={
                  <ProtectedRoute requiredRole="client">
                    <ClientQuotes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/client-portal/payments"
                element={
                  <ProtectedRoute requiredRole="client">
                    <ClientPayments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/client-portal/support"
                element={
                  <ProtectedRoute requiredRole="client">
                    <ClientSupport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/client-portal/ai-assistant"
                element={
                  <ProtectedRoute requiredRole="client">
                    <ClientAIAssistant />
                  </ProtectedRoute>
                }
              />

              {/* Default redirects */}
              <Route
                path="/"
                element={
                  user.role === 'admin' ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <Navigate to="/client-portal" replace />
                  )
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
        
        {/* AI Assistant - Available on all authenticated pages */}
        <AIAssistantButton />
      </div>
    </Router>
  );
}

export default App;